<template>
  <div>
    <div v-if="!printMode" class="mb-3">
      <h6>Chamadas x Instrutor</h6>
      <div class="row g-3 align-items-end">
        <div class="col-auto"><label for="chamadas-mes">Mês de referência</label>
          <input id="chamadas-mes" type="month" class="form-control" v-model="mesAno" /></div>
        <div class="col-auto"><button class="btn btn-primary" :disabled="!mesAno" @click="abrirPrint">Executar</button></div>
      </div>
    </div>
    <div v-else>
      <p v-if="loading">Carregando…</p>
      <p v-else-if="erro" class="alert alert-danger" role="alert">{{ erro }}</p>
      <template v-else-if="dados">
        <section class="mb-3">
          <h6>Resumo — {{ periodoTexto }}</h6>
          <table class="table table-striped table-sm">
            <thead><tr><th>Indicador</th><th>Informação</th></tr></thead>
            <tbody>
              <tr><td>Escola</td><td>{{ dados.escola }}</td></tr>
              <tr><td>Relatório</td><td>Chamadas x Instrutor — {{ periodoTexto }}</td></tr>
              <tr><td>Total de chamadas registradas no mês</td><td>{{ dados.resumo.total_chamadas }}</td></tr>
              <tr><td>Instrutores identificados (nomes registrados)</td><td>{{ dados.resumo.instrutores_identificados }}</td></tr>
              <tr><td>Chamadas sem aluno presente</td><td>{{ dados.resumo.chamadas_sem_presentes }}</td></tr>
              <tr><td>Cabeçalhos sem participantes (excluídos)</td><td>{{ dados.resumo.cabecalhos_vazios }}</td></tr>
            </tbody>
          </table>
        </section>
        <section class="mb-3">
          <h6>Resumo por Instrutor</h6>
          <table class="table table-striped table-sm">
            <thead><tr><th>Instrutor</th><th>Chamadas registradas</th><th>Sem aluno presente</th></tr></thead>
            <tbody>
              <tr v-for="grupo in dados.grupos" :key="grupo.instrutor ?? '__sem_instrutor__'"><td>{{ grupo.instrutor || 'Não identificado' }}</td><td>{{ grupo.total }}</td><td>{{ grupo.sem_presentes }}</td></tr>
              <tr class="fw-bold"><td>TOTAL</td><td>{{ dados.resumo.total_chamadas }}</td><td>{{ dados.resumo.chamadas_sem_presentes }}</td></tr>
            </tbody>
          </table>
        </section>
        <section class="mb-3">
          <h6>Observações</h6>
          <table class="table table-sm text-muted">
            <thead><tr><th>Qualidade dos registros</th></tr></thead>
            <tbody>
              <tr><td>Instrutor conforme nome registrado na chamada. Alterações posteriores e substituições podem não estar refletidas historicamente.</td></tr>
              <tr><td>Não existe marcador de conclusão. Chamadas com participantes podem ter salvamento parcial; chamadas com todos ausentes estão incluídas.</td></tr>
              <tr v-if="dados.resumo.chamadas_sem_instrutor"><td>Alerta: {{ dados.resumo.chamadas_sem_instrutor }} chamada(s) sem instrutor identificado.</td></tr>
              <tr><td>Os totais representam chamadas registradas, não comprovação independente de aulas ministradas.</td></tr>
              <tr v-if="dados.colisoes.some(c => c.possivel_duplicidade)"><td>Possível duplicidade identificada: existem chamadas registradas para a mesma turma, data e horário. Os totais incluem os registros encontrados e podem conter duplicidades.</td></tr>
              <tr v-if="dados.colisoes.some(c => !c.possivel_duplicidade)"><td>Há colisões envolvendo cabeçalhos vazios, excluídos da contagem. Essas colisões não aumentam o total de chamadas contabilizadas.</td></tr>
            </tbody>
          </table>
        </section>
        <section v-if="dados.colisoes.length" class="mb-3">
          <h6>Colisões para revisão ({{ dados.colisoes.length }})</h6>
          <table class="table table-striped table-sm">
            <thead><tr><th>Data da Aula</th><th>Turma</th><th>Horário</th><th>IDs das chamadas</th><th>Dia da semana registrado</th><th>Impacto na contagem</th></tr></thead>
            <tbody><tr v-for="c in dados.colisoes" :key="c.ids.join(',')"><td>{{ dataBR(c.data_aula) }}</td><td>{{ c.turma_id }}</td><td>{{ c.horario }}</td><td>{{ c.ids.join(', ') }}</td><td>{{ c.dias_semana.join(' / ') }}{{ c.dias_inconsistentes ? ' (inconsistente)' : '' }}</td><td>{{ c.possivel_duplicidade ? 'Possível duplicidade entre chamadas elegíveis' : 'Sem aumento por duplicidade' }}. Elegíveis: {{ c.ids_elegiveis.join(', ') || 'nenhum' }}; vazios excluídos: {{ c.ids_vazios.join(', ') || 'nenhum' }}.</td></tr></tbody>
          </table>
        </section>
        <p v-if="!dados.grupos.length">Nenhuma chamada com participantes no período.</p>
        <section v-for="grupo in dados.grupos" :key="grupo.instrutor ?? '__sem_instrutor__'" class="mb-4">
          <h6>Instrutor: {{ grupo.instrutor || 'Não identificado' }} — {{ grupo.total }} chamadas registradas</h6>
          <table class="table table-striped table-sm">
            <thead><tr><th>Data da Aula</th><th>Turma</th><th>Horário</th><th>Observação</th></tr></thead>
            <tbody><tr v-for="aula in grupo.chamadas" :key="aula.chamada_id"><td>{{ dataBR(aula.data_aula) }}</td><td>{{ aula.turma_id }}</td><td>{{ aula.horario }}</td><td class="text-muted">{{ aula.sem_presentes ? 'Sem aluno presente' : '' }}</td></tr></tbody>
          </table>
        </section>
      </template>
    </div>
  </div>
</template>
<script setup>
import { ref, computed, onMounted } from 'vue'
import { useRouter, useRoute } from 'vue-router'
import axios from '@/services/axios'
const props = defineProps({ printMode: { type: Boolean, default: false } })
const emit = defineEmits(['export-ready'])
const router = useRouter(), route = useRoute()
const hoje = new Date()
const mesAno = ref(`${hoje.getFullYear()}-${String(hoje.getMonth()+1).padStart(2,'0')}`)
const dados = ref(null), loading = ref(false), erro = ref('')
const periodoTexto = computed(() => dados.value ? `${String(dados.value.mes).padStart(2,'0')}/${dados.value.ano}` : '')
const dataBR = d => String(d).split('-').reverse().join('/')
function abrirPrint() {
  const [ano,mes] = mesAno.value.split('-')
  router.push({path:'/gestao/relatorios/print/chamadas-instrutor',query:{title:'Chamadas x Instrutor',subtitle:`${mes}/${ano}`,ano,mes}})
}
onMounted(async () => {
  if (!props.printMode) return
  emit('export-ready',false); loading.value=true
  try {
    const res=await axios.get('/gestao/relatorios/operacionais/chamadas-instrutor',{params:{ano:route.query.ano,mes:route.query.mes}})
    dados.value=res.data
    emit('export-ready',true)
  } catch(e) { erro.value=e.response?.data?.error || 'Não foi possível carregar o relatório.' }
  finally { loading.value=false }
})
</script>
<style scoped>
table { width: 100%; font-size: .75rem; }
th, td { vertical-align: top; overflow-wrap: anywhere; }
</style>
